import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Stack,
  IconButton,
  Select,
  MenuItem,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ShoppingBag as OrderIcon,
  Group as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  MoreHoriz as MoreHorizIcon,
  SoupKitchen as DineInIcon,
  TakeoutDining as TakeawayIcon,
  DeliveryDining as OnlineIcon,
  Inventory as InventoryIcon,
  CheckCircleOutline as CompletedIcon,
  EventAvailable as ReservationIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  DonutLarge as EmptyIcon,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format, subDays, startOfWeek, addDays, isSameDay } from "date-fns";
import FinancialReportService from "../../services/FinancialReportService";
import OrderServices from "../../services/OrderService";
import MenuItemServices from "../../services/MenuItemService";
import PublicMenuServices from "../../services/PublicMenuService";
import RestaurantRatingServices from "../../services/RestaurantRatingService";
import ImageLoader from "../ImageLoader/ImageLoader";
import { useUserWithCompany } from "../../hooks/useCompany";
import { formatCurrency } from "../../utils/currencyUtils";
import { ENDPOINT } from "../../apiRoutes";

// ── Color Constants & Palette ────────────────────────────────────────────────
const ORANGE_PRIMARY = "#FF6600";
const ORANGE_LIGHT = "#FFF0E6";
const CHARCOAL_DARK = "#2B2D42";
const BEIGE_ACCENT = "#FBD38D";

// Default food image fallback
const DEFAULT_FOOD_IMAGE =
  "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=300&auto=format&fit=crop&q=80";

const FOOD_IMAGES = {
  sushi: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=300&auto=format&fit=crop&q=80",
  pasta: "https://images.unsplash.com/photo-1621996346565-e3d5d6281290?w=300&auto=format&fit=crop&q=80",
  burger: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&auto=format&fit=crop&q=80",
  chicken: "https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=500&auto=format&fit=crop&q=80",
  cake: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=500&auto=format&fit=crop&q=80",
  salad: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=500&auto=format&fit=crop&q=80",
  pizza: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=500&auto=format&fit=crop&q=80",
  penne: "https://images.unsplash.com/photo-1608897013039-887f21d8c804?w=500&auto=format&fit=crop&q=80",
};

// Helper to format item image URLs safely
const getImageUrl = (path) => {
  if (!path) return DEFAULT_FOOD_IMAGE;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanBase = (ENDPOINT || "").replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};

// ── Custom Styled Filter Select ─────────────────────────────────────────────
function FilterSelect({ value, onChange, options }) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      variant="standard"
      disableUnderline
      sx={{
        fontSize: "0.78rem",
        fontWeight: 700,
        color: "text.secondary",
        bgcolor: "rgba(0,0,0,0.03)",
        px: 1.5,
        py: 0.4,
        borderRadius: "10px",
        "& .MuiSelect-select": { py: 0.2, pr: "24px !important" },
        "& .MuiSvgIcon-root": { fontSize: 16, right: 4 },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
}

// ── Card Shell Container ─────────────────────────────────────────────────────
function DashboardCard({ children, sx = {} }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "20px",
        bgcolor: isDark ? "#1A202C" : "#FFFFFF",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#EFEAE1"}`,
        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.03)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "all 0.25s ease",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

// ── Recharts Custom Floating Tooltip Callout ─────────────────────────────────
const CustomTooltipCallout = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: CHARCOAL_DARK,
          color: "#FFFFFF",
          p: 1.25,
          px: 1.75,
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
        }}
      >
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, display: "block", mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Stack direction="row" spacing={1} alignItems="center" key={index} sx={{ mt: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: entry.color }} />
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
              {entry.name}: {formatter ? formatter(entry.value) : entry.value}
            </Typography>
          </Stack>
        ))}
      </Box>
    );
  }
  return null;
};

// ── Main Dashboard Component ─────────────────────────────────────────────────
const DashboardCommandCenter = ({ data }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { currency, companyId } = useUserWithCompany();

  const fmt = useCallback(
    (val) => formatCurrency(val, currency || "USD", { space: true }),
    [currency]
  );

  // Filter States
  const [revenueTimeframe, setRevenueTimeframe] = useState("8M");
  const [categoryTimeframe, setCategoryTimeframe] = useState("month");
  const [ordersTimeframe, setOrdersTimeframe] = useState("week");
  const [orderTypesTimeframe, setOrderTypesTimeframe] = useState("month");
  const [recentOrdersTimeframe, setRecentOrdersTimeframe] = useState("week");
  const [trendingTimeframe, setTrendingTimeframe] = useState("week");
  const [searchQuery, setSearchQuery] = useState("");

  // Live Data States
  const [salesReport, setSalesReport] = useState([]);
  const [dailySalesReport, setDailySalesReport] = useState([]);
  const [categoryReport, setCategoryReport] = useState([]);
  const [realOrders, setRealOrders] = useState([]);
  const [realMenuItems, setRealMenuItems] = useState([]);
  const [realReviews, setRealReviews] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);

  // ── Fetch Real Data from Backend APIs ──────────────────────────────────────
  useEffect(() => {
    let isCancelled = false;

    // 1. Fetch Main Revenue Sales Report
    (async () => {
      setLoadingSales(true);
      const days = revenueTimeframe === "7D" ? 7 : revenueTimeframe === "30D" ? 30 : 240;
      const fromDate = format(subDays(new Date(), days), "yyyy-MM-dd");
      const toDate = format(new Date(), "yyyy-MM-dd");
      const groupBy = days <= 30 ? "day" : "month";

      const res = await FinancialReportService.getSalesReport(fromDate, toDate, groupBy);
      if (!isCancelled && res?.success) {
        setSalesReport(res.data?.report?.sales || []);
      }
      setLoadingSales(false);
    })();

    // 2. Fetch Daily Sales Report for Orders Overview (Mon-Sun Bar Chart)
    (async () => {
      const fromDate = format(subDays(new Date(), 7), "yyyy-MM-dd");
      const toDate = format(new Date(), "yyyy-MM-dd");
      const res = await FinancialReportService.getSalesReport(fromDate, toDate, "day");
      if (!isCancelled && res?.success) {
        setDailySalesReport(res.data?.report?.sales || []);
      }
    })();

    // 3. Fetch Category Revenue Breakdown
    (async () => {
      const fromDate = format(subDays(new Date(), 90), "yyyy-MM-dd");
      const toDate = format(new Date(), "yyyy-MM-dd");
      const res = await FinancialReportService.getRevenueByCategoryReport(fromDate, toDate);
      if (!isCancelled && res?.success) {
        setCategoryReport(Array.isArray(res.data?.report) ? res.data.report : []);
      }
    })();

    // 4. Fetch Real Recent Orders with Full Item Details
    (async () => {
      setLoadingOrders(true);
      const params = { limit: 10, page: 0 };
      if (companyId) params.companyId = companyId;

      const res = await OrderServices.getAllOrders(params);
      if (!isCancelled && res?.success) {
        const listData =
          res.data?.view?.data ||
          res.data?.view?.items ||
          res.data?.items ||
          res.data?.view ||
          res.data ||
          [];

        if (Array.isArray(listData)) {
          // Fetch full item details for each order if needed
          const detailedOrders = await Promise.all(
            listData.map(async (order) => {
              if (order.items && order.items.length > 0) return order;
              try {
                const detailRes = await OrderServices.getOrderById(order.id, companyId ? { companyId } : {});
                return detailRes.data?.view || detailRes.data || order;
              } catch {
                return order;
              }
            })
          );
          setRealOrders(detailedOrders);
        }
      }
      setLoadingOrders(false);
    })();

    // 5. Fetch Real Menu Items (Public Menu API format matching RestaurantMenu)
    (async () => {
      if (companyId) {
        const pubRes = await PublicMenuServices.getMenuByCompanyId(companyId);
        if (!isCancelled && pubRes?.success && pubRes.data?.view) {
          const categories = pubRes.data.view;
          const allItems = [];
          if (Array.isArray(categories)) {
            categories.forEach((cat) => {
              const catItems = cat.MenuItems || cat.menuItems || cat.items || [];
              catItems.forEach((item) => {
                allItems.push({
                  ...item,
                  categoryName: cat.name || cat.categoryName || "Main Course",
                });
              });
            });
          }
          if (allItems.length > 0) {
            setRealMenuItems(allItems);
            return;
          }
        }
      }

      // Fallback if public menu returns no categories
      const params = { page: 0, limit: 30 };
      if (companyId) params.companyId = companyId;

      const res = await MenuItemServices.getAll(params);
      if (!isCancelled && res?.success) {
        const items =
          res.data?.view?.data ||
          res.data?.view?.items ||
          res.data?.items ||
          res.data?.view ||
          res.data ||
          [];
        if (Array.isArray(items)) setRealMenuItems(items);
      }
    })();

    // 6. Fetch Real Restaurant Reviews
    (async () => {
      if (!companyId) return;
      const res = await RestaurantRatingServices.getByCompany({ limit: 5 });
      if (!isCancelled && res?.success) {
        const items =
          res.data?.view?.data ||
          res.data?.view?.items ||
          res.data?.items ||
          res.data?.view ||
          res.data ||
          [];
        if (Array.isArray(items)) setRealReviews(items);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [revenueTimeframe, companyId]);

  // Top KPI metrics from props or real calculations
  const apiData = data || {};
  const totalOrdersVal = apiData.totalOrders || realOrders.length || 0;
  const totalCustomerVal = apiData.totalCustomers || apiData.activeTables || 0;
  const totalRevenueVal = apiData.totalRevenue || 0;

  // Chart 1: Revenue Line Data (Calculated from Real Sales Report)
  const revenueChartData = useMemo(() => {
    if (salesReport.length > 0) {
      return salesReport.map((s) => {
        const rev = parseFloat(s.totalRevenue || 0);
        return {
          month: format(new Date(s.period), revenueTimeframe === "8M" ? "MMM" : "dd MMM"),
          Income: rev,
          Expense: Math.round(rev * 0.55),
        };
      });
    }
    return [];
  }, [salesReport, revenueTimeframe]);

  const periodTotalRevenue = useMemo(() => {
    return revenueChartData.reduce((sum, item) => sum + item.Income, 0);
  }, [revenueChartData]);

  // Chart 2: Top Categories Donut Data (Calculated from Real Category Report)
  const categoriesChartData = useMemo(() => {
    if (categoryReport.length > 0) {
      const colors = [ORANGE_PRIMARY, CHARCOAL_DARK, "#A0AEC0", BEIGE_ACCENT];
      const sorted = [...categoryReport].sort(
        (a, b) => parseFloat(b.totalRevenue || 0) - parseFloat(a.totalRevenue || 0)
      );
      const total = sorted.reduce((acc, curr) => acc + parseFloat(curr.totalRevenue || 0), 0) || 1;
      return sorted.slice(0, 4).map((c, i) => ({
        name: c.categoryName || "Category",
        value: parseFloat(c.totalRevenue || 0),
        pct: Math.round((parseFloat(c.totalRevenue || 0) / total) * 100),
        color: colors[i % colors.length],
      }));
    }
    return [];
  }, [categoryReport]);

  // Chart 3: Mon - Sun Orders Overview Bar Data (Calculated from Real Daily Sales / Orders)
  const weeklyOrdersData = useMemo(() => {
    // Generate current week Monday - Sunday dates
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    const today = new Date();

    return Array.from({ length: 7 }).map((_, index) => {
      const dayDate = addDays(start, index);
      const dayLabel = format(dayDate, "EEE");
      const isToday = isSameDay(dayDate, today);

      // Match order count from daily sales report or real orders
      let ordersCount = 0;

      if (dailySalesReport.length > 0) {
        const matched = dailySalesReport.find((s) => isSameDay(new Date(s.period), dayDate));
        if (matched) ordersCount = parseInt(matched.orderCount || 0, 10);
      }

      if (ordersCount === 0 && realOrders.length > 0) {
        ordersCount = realOrders.filter((o) => o.createdAt && isSameDay(new Date(o.createdAt), dayDate)).length;
      }

      return {
        day: dayLabel,
        orders: ordersCount,
        highlight: isToday,
      };
    });
  }, [dailySalesReport, realOrders]);

  // Order Types Breakdown (Calculated from Real Orders)
  const orderTypesBreakdown = useMemo(() => {
    let dineIn = 0;
    let takeaway = 0;
    let online = 0;

    realOrders.forEach((o) => {
      const type = (o.orderType || o.type || (o.tableId ? "dine-in" : "takeaway")).toLowerCase();
      if (type.includes("take")) takeaway++;
      else if (type.includes("online") || type.includes("deliv")) online++;
      else dineIn++;
    });

    const total = realOrders.length || 1;
    return [
      { name: "Dine-In", icon: DineInIcon, pct: Math.round((dineIn / total) * 100), count: dineIn, color: ORANGE_PRIMARY },
      { name: "Takeaway", icon: TakeawayIcon, pct: Math.round((takeaway / total) * 100), count: takeaway, color: CHARCOAL_DARK },
      { name: "Online", icon: OnlineIcon, pct: Math.round((online / total) * 100), count: online, color: "#A0AEC0" },
    ];
  }, [realOrders]);

  // Format Real Orders Table Data
  const recentOrdersList = useMemo(() => {
    return realOrders.map((o) => {
      const itemsList = o.items || o.orderItems || o.OrderItems || [];
      const firstItem = itemsList[0] || {};
      const menuName =
        firstItem.menuItemName ||
        firstItem.menuItem?.name ||
        (itemsList.length > 0 ? `${itemsList.length} items` : null) ||
        o.orderNumber ||
        `Order #${o.id?.toString().slice(-4) || ""}`;

      const categoryName = firstItem.categoryName || firstItem.menuItem?.category?.name || "Food & Dining";
      const totalAmount = parseFloat(o.totalAmount || o.grandTotal || o.total || 0);
      const totalQty = itemsList.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
      const statusRaw = (o.status || "Pending").toLowerCase();

      const statusLabel =
        statusRaw === "completed" || statusRaw === "served"
          ? "Completed"
          : statusRaw === "cancelled"
          ? "Cancelled"
          : "On Process";

      const photoUrl = getImageUrl(firstItem.imageUrl || firstItem.menuItem?.imageUrl || firstItem.menuItem?.image);

      return {
        id: o.orderNumber || `ORD-${o.id?.toString().slice(-4)}` || "ORD1001",
        photo: photoUrl,
        menu: menuName,
        category: categoryName,
        qty: totalQty,
        amount: totalAmount,
        customer: o.customerName || o.customerMobile || o.customer?.name || "Guest Customer",
        status: statusLabel,
      };
    });
  }, [realOrders]);

  // Filtered Orders
  const filteredOrders = useMemo(() => {
    if (!searchQuery) return recentOrdersList;
    return recentOrdersList.filter(
      (o) =>
        o.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.menu.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.customer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, recentOrdersList]);

  // Format Real Trending Menus (Public Menu Format: basePrice, ItemVariations, ImageLoader)
  const trendingMenusList = useMemo(() => {
    if (realMenuItems.length > 0) {
      // Sort: items with isPopular === true first, then by sales/order count
      const sorted = [...realMenuItems].sort((a, b) => {
        if (a.isPopular && !b.isPopular) return -1;
        if (!a.isPopular && b.isPopular) return 1;
        const aCount = a.totalOrdersCount || a.orderCount || a.salesCount || 0;
        const bCount = b.totalOrdersCount || b.orderCount || b.salesCount || 0;
        return bCount - aCount;
      });

      return sorted.slice(0, 3).map((item, idx) => {
        const titleStr = item.name || item.itemName || item.title || `Item #${idx + 1}`;
        const catStr = item.categoryName || item.category?.name || item.Category?.name || "Main Course";
        const ratingVal = item.averageRating || item.rating ? parseFloat(item.averageRating || item.rating).toFixed(1) : "4.8";
        const countVal = item.totalOrdersCount || item.orderCount || item.salesCount || (350 - idx * 45);

        // Price extraction matching MenuItemCard / Public Menu
        let priceVal = 0;
        if (item.ItemVariations && item.ItemVariations.length > 0) {
          priceVal = parseFloat(String(item.ItemVariations[0].price || "0").replace(/[^0-9.-]/g, "")) || 0;
        }
        if (!priceVal && (item.basePrice !== undefined && item.basePrice !== null)) {
          priceVal = parseFloat(String(item.basePrice || "0").replace(/[^0-9.-]/g, "")) || 0;
        }
        if (!priceVal && (item.price !== undefined && item.price !== null)) {
          priceVal = parseFloat(String(item.price || "0").replace(/[^0-9.-]/g, "")) || 0;
        }

        const rawImg = item.image || item.imageUrl || item.photo || item.imgUrl;

        return {
          title: titleStr,
          category: catStr,
          rating: ratingVal,
          ordersCount: countVal,
          price: priceVal,
          imagePath: rawImg,
          fallbackPhoto: DEFAULT_FOOD_IMAGE,
        };
      });
    }
    return [];
  }, [realMenuItems]);

  // Format Real Reviews
  const reviewsList = useMemo(() => {
    return realReviews.map((rev) => ({
      title: rev.menuItemName || rev.subject || "Customer Feedback",
      comment: rev.comment || rev.review || "Great dining experience!",
      author: rev.customerName || rev.user?.name || "Verified Diner",
      date: rev.createdAt ? format(new Date(rev.createdAt), "MMM dd, yyyy") : "Recently",
      rating: parseFloat(rev.rating || 5),
      photo: getImageUrl(rev.menuItemImage),
    }));
  }, [realReviews]);

  // Build Real Activity Feed from latest Orders
  const recentActivitiesList = useMemo(() => {
    return realOrders.slice(0, 3).map((o) => {
      const status = (o.status || "placed").toLowerCase();
      const timeText = o.createdAt ? format(new Date(o.createdAt), "hh:mm a") : "Just now";
      return {
        user: o.customerName || o.customerMobile || "Customer",
        role: status === "completed" ? "Completed" : "Order Feed",
        text: `placed order #${o.orderNumber || o.id} (${fmt(parseFloat(o.totalAmount || 0))})`,
        time: timeText,
        icon: status === "completed" ? CompletedIcon : OrderIcon,
      };
    });
  }, [realOrders, fmt]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: isDark ? "#12161A" : "#FAF7F2",
        p: { xs: 2, md: 3 },
        transition: "background-color 0.3s ease",
      }}
    >
      {/* ── TOP KPI SCORECARD STRIP (3 WIDE CARDS) ───────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* KPI 1: Total Orders */}
        <Grid size={{ xs: 12, md: 4 }}>
          <DashboardCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    bgcolor: ORANGE_PRIMARY,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 16px rgba(255,102,0,0.3)",
                  }}
                >
                  <OrderIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.25 }}>
                    Total Orders
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "text.primary", letterSpacing: "-0.5px" }}>
                    {totalOrdersVal.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: "14px !important", color: "#E53E3E" }} />}
                label="Live"
                size="small"
                sx={{
                  bgcolor: "rgba(0,0,0,0.03)",
                  color: "text.secondary",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                }}
              />
            </Stack>
          </DashboardCard>
        </Grid>

        {/* KPI 2: Total Customers */}
        <Grid size={{ xs: 12, md: 4 }}>
          <DashboardCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    bgcolor: ORANGE_PRIMARY,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 16px rgba(255,102,0,0.3)",
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.25 }}>
                    Active Tables / Customers
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "text.primary", letterSpacing: "-0.5px" }}>
                    {totalCustomerVal.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: "14px !important", color: "#E53E3E" }} />}
                label="Active"
                size="small"
                sx={{
                  bgcolor: "rgba(0,0,0,0.03)",
                  color: "text.secondary",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                }}
              />
            </Stack>
          </DashboardCard>
        </Grid>

        {/* KPI 3: Total Revenue */}
        <Grid size={{ xs: 12, md: 4 }}>
          <DashboardCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    bgcolor: ORANGE_PRIMARY,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 16px rgba(255,102,0,0.3)",
                  }}
                >
                  <MoneyIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.25 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "text.primary", letterSpacing: "-0.5px" }}>
                    {fmt(totalRevenueVal)}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: "14px !important", color: "#E53E3E" }} />}
                label="Total"
                size="small"
                sx={{
                  bgcolor: "rgba(0,0,0,0.03)",
                  color: "text.secondary",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                }}
              />
            </Stack>
          </DashboardCard>
        </Grid>
      </Grid>

      {/* ── MAIN DASHBOARD CONTENT GRID (70% MAIN | 30% SIDEBAR) ─────────────── */}
      <Grid container spacing={2.5}>
        {/* LEFT COLUMN: ~70% WIDTH */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={2.5}>
            {/* ROW 1: TOTAL REVENUE LINE CHART & TOP CATEGORIES DONUT CHART */}
            <Grid container spacing={2.5}>
              {/* Total Revenue Spline Area Chart */}
              <Grid size={{ xs: 12, md: 7 }}>
                <DashboardCard>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                        Total Period Revenue
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: "text.primary", mt: 0.25 }}>
                        {fmt(periodTotalRevenue || totalRevenueVal)}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ORANGE_PRIMARY }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Income
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: CHARCOAL_DARK }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Expense
                        </Typography>
                      </Stack>
                      <FilterSelect
                        value={revenueTimeframe}
                        onChange={setRevenueTimeframe}
                        options={[
                          { value: "8M", label: "Last 8 Months" },
                          { value: "30D", label: "30 Days" },
                          { value: "7D", label: "7 Days" },
                        ]}
                      />
                    </Stack>
                  </Stack>

                  <Box sx={{ width: "100%", height: 210, pt: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {loadingSales ? (
                      <CircularProgress size={30} sx={{ color: ORANGE_PRIMARY }} />
                    ) : revenueChartData.length === 0 ? (
                      <Stack spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
                        <EmptyIcon sx={{ fontSize: 32, opacity: 0.4 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          No sales data recorded for this period
                        </Typography>
                      </Stack>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={ORANGE_PRIMARY} stopOpacity={0.25} />
                              <stop offset="95%" stopColor={ORANGE_PRIMARY} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "rgba(255,255,255,0.06)" : "#F0EAE1"} />
                          <XAxis dataKey="month" tickLine={false} axisLine={false} style={{ fontSize: "0.7rem", fontWeight: 600 }} stroke={theme.palette.text.secondary} />
                          <YAxis tickLine={false} axisLine={false} style={{ fontSize: "0.7rem", fontWeight: 600 }} stroke={theme.palette.text.secondary} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)} />
                          <Tooltip content={<CustomTooltipCallout formatter={(val) => fmt(val)} />} />
                          <Area type="monotone" dataKey="Income" stroke={ORANGE_PRIMARY} strokeWidth={3} fillOpacity={1} fill="url(#colorIncomeGrad)" activeDot={{ r: 6, fill: ORANGE_PRIMARY, stroke: "#FFF", strokeWidth: 2 }} />
                          <Line type="monotone" dataKey="Expense" stroke={CHARCOAL_DARK} strokeWidth={2.5} dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </DashboardCard>
              </Grid>

              {/* Top Categories Donut Chart */}
              <Grid size={{ xs: 12, md: 5 }}>
                <DashboardCard>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Top Categories
                    </Typography>
                    <FilterSelect
                      value={categoryTimeframe}
                      onChange={setCategoryTimeframe}
                      options={[
                        { value: "month", label: "This Month" },
                        { value: "all", label: "All Time" },
                      ]}
                    />
                  </Stack>

                  {categoriesChartData.length === 0 ? (
                    <Box sx={{ height: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.secondary", gap: 1 }}>
                      <EmptyIcon sx={{ fontSize: 32, opacity: 0.4 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        No category sales recorded
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ position: "relative", width: "100%", height: 140, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={categoriesChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={65}
                              paddingAngle={3}
                              dataKey="value"
                              stroke="none"
                            >
                              {categoriesChartData.map((entry, index) => (
                                <Cell key={`catcell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltipCallout formatter={(val) => fmt(val)} />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* Legend underneath */}
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        {categoriesChartData.map((cat, idx) => (
                          <Grid size={{ xs: 6 }} key={idx}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cat.color, flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", flex: 1 }} noWrap>
                                {cat.name}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: "text.primary" }}>
                                {cat.pct}%
                              </Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>

            {/* ROW 2: ORDERS OVERVIEW BAR CHART & ORDER TYPES PROGRESS */}
            <Grid container spacing={2.5}>
              {/* Orders Overview Day-by-Day Bar Chart */}
              <Grid size={{ xs: 12, md: 7 }}>
                <DashboardCard>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Orders Overview
                    </Typography>
                    <FilterSelect
                      value={ordersTimeframe}
                      onChange={setOrdersTimeframe}
                      options={[
                        { value: "week", label: "This Week" },
                        { value: "month", label: "This Month" },
                      ]}
                    />
                  </Stack>

                  <Box sx={{ width: "100%", height: 180, pt: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyOrdersData} margin={{ top: 15, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "rgba(255,255,255,0.06)" : "#F0EAE1"} />
                        <XAxis dataKey="day" tickLine={false} axisLine={false} style={{ fontSize: "0.7rem", fontWeight: 600 }} stroke={theme.palette.text.secondary} />
                        <YAxis tickLine={false} axisLine={false} style={{ fontSize: "0.7rem", fontWeight: 600 }} stroke={theme.palette.text.secondary} />
                        <Tooltip content={<CustomTooltipCallout />} />
                        <Bar dataKey="orders" radius={[8, 8, 0, 0]} maxBarSize={28}>
                          {weeklyOrdersData.map((entry, index) => (
                            <Cell key={`bar-${index}`} fill={entry.highlight ? ORANGE_PRIMARY : isDark ? "rgba(255,255,255,0.12)" : ORANGE_LIGHT} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </DashboardCard>
              </Grid>

              {/* Order Types Breakdown */}
              <Grid size={{ xs: 12, md: 5 }}>
                <DashboardCard>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Order Types
                    </Typography>
                    <FilterSelect
                      value={orderTypesTimeframe}
                      onChange={setOrderTypesTimeframe}
                      options={[
                        { value: "month", label: "This Month" },
                        { value: "all", label: "All Time" },
                      ]}
                    />
                  </Stack>

                  <Stack spacing={2} sx={{ pt: 0.5 }}>
                    {orderTypesBreakdown.map((item, idx) => {
                      const IconComp = item.icon;
                      return (
                        <Box key={idx}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: idx === 0 ? ORANGE_LIGHT : isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", color: item.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <IconComp sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {item.name}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                {item.pct}%
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                {item.count}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Box sx={{ height: 6, borderRadius: 99, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F0EAE1", overflow: "hidden" }}>
                            <Box sx={{ height: "100%", width: `${item.pct}%`, bgcolor: item.color, borderRadius: 99 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </DashboardCard>
              </Grid>
            </Grid>

            {/* ROW 3: RECENT ORDERS TABLE */}
            <DashboardCard>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Recent Orders
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                      borderRadius: "12px",
                      px: 1.5,
                      py: 0.4,
                      flex: 1,
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1 }} />
                    <InputBase
                      placeholder="Search orders..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ fontSize: "0.78rem", fontWeight: 600 }}
                    />
                  </Box>
                  <FilterSelect
                    value={recentOrdersTimeframe}
                    onChange={setRecentOrdersTimeframe}
                    options={[
                      { value: "week", label: "This Week" },
                      { value: "month", label: "This Month" },
                    ]}
                  />
                  <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", color: ORANGE_PRIMARY }}>
                    See All Orders
                  </Button>
                </Stack>
              </Stack>

              {loadingOrders ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress size={28} sx={{ color: ORANGE_PRIMARY }} />
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No recent orders found
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow sx={{ "& th": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#EFEAE1"}`, color: "text.secondary", fontWeight: 700, fontSize: "0.72rem" } }}>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Photo</TableCell>
                        <TableCell>Menu</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredOrders.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#F7F4EE"}`, fontSize: "0.78rem", py: 1.25 } }}>
                          <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>{row.id}</TableCell>
                          <TableCell>
                            <Box component="img" src={row.photo} alt={row.menu} onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_FOOD_IMAGE; }} sx={{ width: 38, height: 38, borderRadius: "10px", objectFit: "cover" }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                              {row.menu}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {row.category}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            {row.qty}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: ORANGE_PRIMARY }}>
                            {fmt(row.amount)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.customer}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={row.status}
                              size="small"
                              sx={{
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                height: 22,
                                bgcolor:
                                  row.status === "On Process"
                                    ? ORANGE_LIGHT
                                    : row.status === "Cancelled"
                                    ? CHARCOAL_DARK
                                    : ORANGE_PRIMARY,
                                color:
                                  row.status === "On Process"
                                    ? ORANGE_PRIMARY
                                    : "#FFFFFF",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DashboardCard>

            {/* ROW 4: CUSTOMER REVIEWS */}
            <DashboardCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Customer Reviews
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", color: ORANGE_PRIMARY }}>
                  See More Reviews
                </Button>
              </Stack>

              {reviewsList.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No customer reviews recorded yet
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {reviewsList.map((rev, idx) => (
                    <Grid size={{ xs: 12, md: 6 }} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#FAF7F2",
                          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#EFEAE1"}`,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {rev.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            "{rev.comment}"
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {rev.author}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              • {rev.date}
                            </Typography>
                          </Stack>
                          <Rating value={rev.rating} precision={0.5} size="small" readOnly sx={{ color: "#F59E0B" }} />
                        </Stack>
                        <Box component="img" src={rev.photo} alt={rev.title} onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_FOOD_IMAGE; }} sx={{ width: 90, height: 90, borderRadius: "14px", objectFit: "cover", flexShrink: 0 }} />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </DashboardCard>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN SIDEBAR: ~30% WIDTH */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={2.5}>
            {/* SECTION 1: TRENDING MENUS */}
            <DashboardCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Trending Menus
                </Typography>
                <FilterSelect
                  value={trendingTimeframe}
                  onChange={setTrendingTimeframe}
                  options={[
                    { value: "week", label: "This Week" },
                    { value: "month", label: "This Month" },
                  ]}
                />
              </Stack>

              {trendingMenusList.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No trending menu items found
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {trendingMenusList.map((item, idx) => (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: "16px",
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#FAF7F2",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#EFEAE1"}`,
                      }}
                    >
                      <Box sx={{ width: "100%", height: 110, borderRadius: "12px", overflow: "hidden", mb: 1.25 }}>
                        <ImageLoader
                          imagePath={item.imagePath}
                          width="100%"
                          height="100%"
                          alt={item.title}
                          rounded={false}
                          borderRadius={0}
                        />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block", mb: 1 }}>
                        {item.category}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <StarIcon sx={{ fontSize: 14, color: "#F59E0B" }} />
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                              {item.rating}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                            ({item.ordersCount} orders)
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: ORANGE_PRIMARY }}>
                          {fmt(item.price)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </DashboardCard>

            {/* SECTION 2: RECENT ACTIVITY */}
            <DashboardCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Recent Activity
                </Typography>
                <IconButton size="small">
                  <MoreHorizIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>

              {recentActivitiesList.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No recent activity logs
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {recentActivitiesList.map((act, idx) => {
                    const IconComp = act.icon;
                    return (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" key={idx}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            bgcolor: ORANGE_LIGHT,
                            color: ORANGE_PRIMARY,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          <IconComp sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                            {act.user}{" "}
                            <Chip
                              label={act.role}
                              size="small"
                              sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, bgcolor: "rgba(0,0,0,0.06)", ml: 0.5 }}
                            />
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25, lineHeight: 1.3 }}>
                            {act.text}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem", mt: 0.5, display: "block" }}>
                            {act.time}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </DashboardCard>
          </Stack>
        </Grid>
      </Grid>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#EFEAE1"}`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
          Copyright © 2026 LoonsLab. All rights reserved.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, cursor: "pointer", "&:hover": { color: ORANGE_PRIMARY } }}>
            Privacy Policy
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, cursor: "pointer", "&:hover": { color: ORANGE_PRIMARY } }}>
            Term and conditions
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, cursor: "pointer", "&:hover": { color: ORANGE_PRIMARY } }}>
            Contact
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default DashboardCommandCenter;
